import { useEffect } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { isEmpty } from 'lodash';

import { StaticError } from 'components/ErrorBoundary';
import Timestamp from 'components/Timestamp';
import {
  AvatarUpdate,
  Carousel,
  CarouselCard,
  Information,
  QuickReplies,
} from 'components/MessageExtensions';
import { CarouselItemType } from 'components/MessageExtensions/Carousel/types';
import { useAppConfig } from 'hooks/appConfig';
import { useConversation } from 'hooks/conversation';

import { htmlId } from 'utils/html';

import { FormMessage } from './FormMessage';
import WithDelayedAnimation from './WithDelayedAnimation/WithDelayedAnimation';
import { AuthorHeader } from './GetAuthorHeader';

import { BaseExtensionMessageProps, ExtensionMessageProps } from './types';
import { TEXT_FORMAT } from './constants';
import { MESSAGE_EXTENSION } from 'constants/message';
import { FORM_TEMPLATE_TYPES } from 'constants/form';
import { StyledMessageWrapper } from './styles';
import FormattedTextMessage from 'components/FormattedTextMessage';

/**
 * (System) Extension message contains a nested list of even more messages. These nested
 * messages have potential to be extensions as well. All data should have already been
 * formatted from Twilio to Carvana's context.
 */
export const ExtensionMessage = ({
  extension,
  isAnimateMsg,
  isLastTwilioMsg,
  twilioMessageIndex,
}: ExtensionMessageProps) => {
  const { showBoundary } = useErrorBoundary();
  const { conversationState } = useConversation();

  // TODO: Remove once Twilio no longer sends this form type.
  // It is causing the app to crash.
  // see also src\constants\form.ts
  if (JSON.stringify(extension).includes(FORM_TEMPLATE_TYPES.LEAD_GEN_FORM)) return null;

  let extensionToParse = extension?.messageBodies?.list ?? [];

  if (!extension.messageBodies) {
    // NOTE: message template (for testing) likely encountered. Schema needs slight adjustment.
    // Be aware that data within may also be inconsistent. Please try to improve over time.
    const extensionEnums = Object.values(MESSAGE_EXTENSION);
    const templateName = Object.keys(extension)[0];
    // @ts-ignore //eslint-disable-next-line
    if (extensionEnums.includes(templateName)) extensionToParse = [{ extension }];
  }

  if (extensionToParse) {
    let timestampIndex = extensionToParse.length - 1;
    while (timestampIndex > 0 && !isEmpty(extensionToParse[timestampIndex]?.extension)) {
      timestampIndex--;
    }

    const twilioMessage = conversationState.messages[twilioMessageIndex];

    // product edge case requirements - hide author for these messages
    const isEdgeCaseQuickReplies = Boolean(
      twilioMessage.extension?.[MESSAGE_EXTENSION.QUICK_REPLIES],
    );
    const isEdgeCaseRatingForm = Boolean(
      twilioMessage.extension.form?.templateType === FORM_TEMPLATE_TYPES.RATING_REQUEST,
    );
    const isEdgeCaseAvatarUpdate = Boolean(
      JSON.stringify(twilioMessage.extension).includes(MESSAGE_EXTENSION.AVATAR_UPDATE),
    );
    const isHideAuthor = isEdgeCaseQuickReplies || isEdgeCaseRatingForm || isEdgeCaseAvatarUpdate;

    return (
      <StyledMessageWrapper
        id={htmlId(`message-container-${twilioMessageIndex}`)}
        data-message-type='extension'
      >
        {!isHideAuthor && <AuthorHeader author={twilioMessage.author} />}
        <WithDelayedAnimation relativeIndex={0} animate={isAnimateMsg}>
          {extensionToParse.map((extension, index) => (
            <BaseExtensionMessage
              author={twilioMessage.author}
              extensionDataItem={extension}
              key={`${twilioMessageIndex}-${index}`}
              twilioMessageIndex={twilioMessageIndex}
              extensionRelativeIndex={index}
              isAnimateMsg={isAnimateMsg}
              isLastMessage={isLastTwilioMsg}
              isEdgeCaseAvatarUpdate={isEdgeCaseAvatarUpdate}
              isMessageComboTimestamp={index === timestampIndex}
              timestamp={twilioMessage.timestamp}
              message={twilioMessage}
            />
          ))}
        </WithDelayedAnimation>
      </StyledMessageWrapper>
    );
  }

  const err = new StaticError(
    `Unknown/incompatible extension encountered: ${JSON.stringify(extension)}`,
  );
  showBoundary(err);
  return null;
};

const BaseExtensionMessage = (props: BaseExtensionMessageProps) => {
  const {
    author,
    timestamp,
    twilioMessageIndex,
    extensionDataItem,
    extensionRelativeIndex,
    isAnimateMsg,
    isLastMessage,
    isEdgeCaseAvatarUpdate,
    isMessageComboTimestamp,
    message,
  } = props;
  const { appConfigState } = useAppConfig();
  const { showBoundary } = useErrorBoundary();

  const { text, extension } = extensionDataItem;
  const extensionUniqueKey = `${twilioMessageIndex}-${extensionRelativeIndex}`;

  useEffect(() => {
    if (appConfigState.isDebugMode) {
      console.log(
        `ChatUi debug - extension message index #${extensionUniqueKey} to parse:`,
        extensionDataItem,
      );
    }
  }, []);

  let MainContent = null;
  let extensionType = null;

  if (!text && !extension) return null;

  if (text && !extension) {
    extensionType = 'text';

    MainContent = (
      <>
        {isEdgeCaseAvatarUpdate && <AuthorHeader author={author} />}
        <FormattedTextMessage
          raw={text}
          format={extensionDataItem.textFormat ?? TEXT_FORMAT.LEGACY}
          message={message}
        />
        {isMessageComboTimestamp && <Timestamp date={timestamp} align='left' />}
      </>
    );
  }

  let isEdgeCaseFormExtension = false;
  if (extension) {
    extensionType = Object.keys(extension)[0]; // follow Twilio data schema
    switch (extensionType) {
      case MESSAGE_EXTENSION.AVATAR_UPDATE: {
        MainContent = (
          <AvatarUpdate
            key={extensionUniqueKey}
            extensionUniqueKey={extensionUniqueKey}
            name={extension?.avatarUpdate.name}
            activityState={extension?.avatarUpdate.state}
          />
        );
        break;
      }
      case MESSAGE_EXTENSION.CAROUSEL: {
        MainContent = (
          <Carousel
            key={extensionUniqueKey}
            extensionUniqueKey={extensionUniqueKey}
            content={extension.carousel.items.map((item: CarouselItemType) => (
              <CarouselCard
                key={`carousel-card-${item.info?.vehicleId ?? item.templateType}`}
                vehicleInformation={item}
              />
            ))}
          />
        );
        break;
      }
      case MESSAGE_EXTENSION.FORM: {
        isEdgeCaseFormExtension = true;
        MainContent = (
          <FormMessage
            key={extensionUniqueKey}
            form={extension.form}
            twilioMessageIndex={twilioMessageIndex}
          />
        );
        break;
      }
      case MESSAGE_EXTENSION.INFORMATION: {
        MainContent = (
          <>
            <Information
              key={extensionUniqueKey}
              extensionUniqueKey={extensionUniqueKey}
              content={extension.information}
            />
            {isMessageComboTimestamp && <Timestamp date={timestamp} align='left' />}
          </>
        );
        break;
      }
      case MESSAGE_EXTENSION.QUICK_REPLIES: {
        // product requirement. Only render quickReplies if last message.
        if (!isLastMessage) return null;
        MainContent = (
          <QuickReplies
            key={extensionUniqueKey}
            content={extension.quickReplies}
            twilioMessageIndex={twilioMessageIndex}
          />
        );
        break;
      }
      default: {
        const err = new StaticError(
          `Unknown message extension encountered: ${JSON.stringify(extensionDataItem)}`,
        );
        showBoundary(err);
        return null;
      }
    }
  }

  return (
    <StyledMessageWrapper
      key={extensionUniqueKey}
      id={htmlId(`message-extension-${extensionUniqueKey}`)}
      data-message-extension-type={extensionType}
      $isMessageComboTimestamp={isMessageComboTimestamp}
      $extensionType={extensionType}
    >
      {extensionRelativeIndex === 0 ? (
        MainContent
      ) : (
        <WithDelayedAnimation
          animate={isAnimateMsg}
          key={`${extensionUniqueKey}-with-delay`}
          relativeIndex={extensionRelativeIndex}
          showIndicator={isEdgeCaseFormExtension ? false : isLastMessage}
        >
          {MainContent}
        </WithDelayedAnimation>
      )}
    </StyledMessageWrapper>
  );
};
